import React, { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  normalizeRegionId,
  normalizeRidingLookupKey,
  toRidingIdLookupKey,
  toRidingNameLookupKey,
} from "../../utils/regionLookup";
import { computeStatistics } from "../Statistics/utils";

/* ──────────────────────────────────────────
 * Province lookup helpers
 * ────────────────────────────────────────*/
const provinceIdToName = {
  1: "Alberta",
  2: "British Columbia",
  3: "Manitoba",
  4: "New Brunswick",
  5: "Newfoundland and Labrador",
  6: "Northwest Territories",
  7: "Nova Scotia",
  8: "Nunavut",
  9: "Ontario",
  10: "Prince Edward Island",
  11: "Quebec",
  12: "Saskatchewan",
  13: "Yukon",
};
const provinceNameToId = Object.fromEntries(
  Object.entries(provinceIdToName).map(([id, name]) => [name, Number(id)])
);

/* ──────────────────────────────────────────
 * Colour ramps – percent-based
 * ────────────────────────────────────────*/
const getColorProvince = (pct) =>
  pct > 90
    ? "#800026"
    : pct > 75
      ? "#BD0026"
      : pct > 60
        ? "#E31A1C"
        : pct > 45
          ? "#FC4E2A"
          : pct > 30
            ? "#FD8D3C"
            : pct > 15
              ? "#FEB24C"
              : pct > 5
                ? "#FED976"
                : "#FFEDA0";

const getColorRiding = getColorProvince;

const highlightStyle = {
  weight: 3,
  color: "#ff7800",
  fillOpacity: 0.5,
  fillColor: "#ff7800",
};

/* Some GeoJSONs only include FEDNUM inside an HTML description cell */
const extractFeduid = (description = "") => {
  const doc = new DOMParser().parseFromString(description, "text/html");
  const cell = [...doc.querySelectorAll("tr td:first-child")].find(
    (td) => td.textContent.trim() === "FEDNUM"
  );
  return cell ? parseInt(cell.nextElementSibling.textContent, 10) : null;
};

// Robust getter for riding id across schema variations
const getFeduid = (feature) => {
  const props = feature?.properties || {};
  return (
    props.feduid ?? props.FEDUID ?? props.fednum ?? props.FEDNUM ?? extractFeduid(props.description)
  );
};

const getRidingFeatureLookupKeys = (feature) => {
  const props = feature?.properties || {};
  const keys = new Set();

  const idKey = toRidingIdLookupKey(getFeduid(feature));
  if (idKey) keys.add(idKey);

  [props.name, props.ED_NAME, props.ENNAME].forEach((nameValue) => {
    const nameKey = toRidingNameLookupKey(nameValue);
    if (nameKey) keys.add(nameKey);
  });

  return Array.from(keys);
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const formatCountValue = (value, useWeightedData) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  if (!useWeightedData) return Math.round(numeric).toLocaleString();
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const sortMappingEntries = (mapping = {}) =>
  Object.entries(mapping).sort(([leftKey], [rightKey]) => {
    const leftNum = Number(leftKey);
    const rightNum = Number(rightKey);
    if (Number.isFinite(leftNum) && Number.isFinite(rightNum)) return leftNum - rightNum;
    return String(leftKey).localeCompare(String(rightKey));
  });

const normalizeAnswerCode = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";

  const numeric = Number(text);
  if (Number.isFinite(numeric)) return String(numeric);

  return text;
};

const formatStatValue = (value, digits = 1) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  return numeric.toFixed(digits);
};

const InteractiveMap = ({
  provinceCounts,
  ridingCounts,
  viewMode,
  answerDisplayMap,
  useWeightedData,
  depType,
  outcomeKey,
}) => {
  const [geoJsonData, setGeoJsonData] = useState(null);
  const provinceBucketsById = useMemo(
    () => new Map(provinceCounts.map((bucket) => [bucket.province, bucket])),
    [provinceCounts]
  );
  const ridingBucketsByLookup = useMemo(() => {
    const lookup = new Map();
    ridingCounts.forEach((bucket) => {
      const keys = new Set((bucket.lookupKeys || []).map(normalizeRidingLookupKey).filter(Boolean));
      const ridingIdKey = toRidingIdLookupKey(bucket.riding);
      const ridingNameKey = toRidingNameLookupKey(bucket.ridingName);
      if (ridingIdKey) keys.add(ridingIdKey);
      if (ridingNameKey) keys.add(ridingNameKey);

      keys.forEach((key) => {
        if (!lookup.has(key)) lookup.set(key, bucket);
      });
    });
    return lookup;
  }, [ridingCounts]);

  const getRidingBucket = useCallback(
    (feature) => {
      const keys = getRidingFeatureLookupKeys(feature);
      for (const key of keys) {
        const bucket = ridingBucketsByLookup.get(key);
        if (bucket) return bucket;
      }
      return null;
    },
    [ridingBucketsByLookup]
  );

  const getProvinceBucket = useCallback(
    (feature) => {
      const provId = provinceNameToId[feature?.properties?.prov_name_en];
      return provinceBucketsById.get(provId) || null;
    },
    [provinceBucketsById]
  );

  const getActiveBucket = useCallback(
    (feature) => (viewMode === "province" ? getProvinceBucket(feature) : getRidingBucket(feature)),
    [viewMode, getProvinceBucket, getRidingBucket]
  );
  const orderedAnswerEntries = useMemo(
    () =>
      sortMappingEntries(answerDisplayMap).map(([answerId, meta]) => ({
        id: String(answerId),
        label: String(meta?.Display || answerId).trim(),
      })),
    [answerDisplayMap]
  );

  /* Load correct GeoJSON whenever the mode flips */
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    setGeoJsonData(null);

    (async () => {
      try {
        const url = viewMode === "province" ? "/georef-canada-province.geojson" : "/riding.json";
        const res = await fetch(url, { signal: controller.signal });
        const data = await res.json();
        if (isMounted) setGeoJsonData(data);
      } catch (err) {
        if (err?.name !== "AbortError" && isMounted) {
          setGeoJsonData(null);
        }
      }
    })();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [viewMode]);

  /* Style by percent */
  const styleFeature = useCallback(
    (feature) => {
      const bucket = getActiveBucket(feature);
      const pct = bucket?.freq || 0;
      return {
        weight: 1,
        color: "#3388ff",
        fillOpacity: 0.7,
        fillColor: viewMode === "province" ? getColorProvince(pct) : getColorRiding(pct),
      };
    },
    [viewMode, getActiveBucket]
  );

  const onEachFeature = (feature, layer) => {
    const makeTooltip = (name, bucket) => {
      const safeName = escapeHtml(name);
      if (!bucket) {
        return `
          <div class="map-tooltip-card">
            <p class="map-tooltip-title">${safeName}</p>
            <p class="map-tooltip-subtle">Total Count: 0</p>
          </div>
        `;
      }

      const total = Number(bucket?.total) || 0;
      const byResp = bucket?.byResp || {};
      const isContinuous = String(depType || "").toLowerCase() === "num";

      if (isContinuous) {
        const points = Object.entries(byResp)
          .map(([answerCode, count]) => ({
            value: Number(String(answerCode).trim()),
            weight: Number(count),
          }))
          .filter(
            (point) =>
              Number.isFinite(point.value) && Number.isFinite(point.weight) && point.weight > 0
          );
        const stats = computeStatistics(points, "value", {
          weightAccessor: (point) => point.weight,
        });

        return `
          <div class="map-tooltip-card">
            <p class="map-tooltip-title">${safeName}</p>
            <p class="map-tooltip-subtle">Total Count: ${formatCountValue(total, useWeightedData)}</p>
            <div class="map-tooltip-divider"></div>
            <div class="map-tooltip-row"><span class="map-tooltip-answer"><strong>Average:</strong></span><span class="map-tooltip-value">${formatStatValue(stats.average)}</span></div>
            <div class="map-tooltip-row"><span class="map-tooltip-answer"><strong>Median:</strong></span><span class="map-tooltip-value">${formatStatValue(stats.median)}</span></div>
            <div class="map-tooltip-row"><span class="map-tooltip-answer"><strong>Std. Deviation:</strong></span><span class="map-tooltip-value">${formatStatValue(stats.stdDev)}</span></div>
            <div class="map-tooltip-row"><span class="map-tooltip-answer"><strong>Range:</strong></span><span class="map-tooltip-value">${stats.min} - ${stats.max}</span></div>
          </div>
        `;
      }

      const orderedEntriesByNormalizedId = new Map(
        orderedAnswerEntries.map((entry) => [normalizeAnswerCode(entry.id), entry])
      );
      const responseRowsById = new Map();

      Object.entries(byResp).forEach(([answerId, count]) => {
        const numericCount = Number(count) || 0;
        if (numericCount <= 0) return;

        const normalizedId = normalizeAnswerCode(answerId);
        const mappedEntry = orderedEntriesByNormalizedId.get(normalizedId);
        const rowId = mappedEntry?.id || normalizedId || String(answerId);
        const rowLabel =
          mappedEntry?.label ||
          String(
            answerDisplayMap?.[answerId]?.Display || answerDisplayMap?.[rowId]?.Display || answerId
          );
        const existing = responseRowsById.get(rowId) || {
          id: rowId,
          label: rowLabel,
          count: 0,
        };

        existing.count += numericCount;
        responseRowsById.set(rowId, existing);
      });

      const responseRows = Array.from(responseRowsById.values()).sort(
        (left, right) => right.count - left.count
      );
      const visibleRows = responseRows.slice(0, 6);
      const extraCount = Math.max(0, responseRows.length - visibleRows.length);

      const rowsHtml = visibleRows
        .map((entry) => {
          const responsePct = total > 0 ? (entry.count / total) * 100 : 0;
          return `
            <div class="map-tooltip-row">
              <span class="map-tooltip-answer">${escapeHtml(entry.label)}</span>
              <span class="map-tooltip-value">
                ${formatCountValue(entry.count, useWeightedData)} (${responsePct.toFixed(1)}%)
              </span>
            </div>
          `;
        })
        .join("");

      const extraHtml =
        extraCount > 0 ? `<p class="map-tooltip-more">+${extraCount} more responses</p>` : "";

      return `
        <div class="map-tooltip-card">
          <p class="map-tooltip-title">${safeName}</p>
          <p class="map-tooltip-subtle">Total Count: ${formatCountValue(total, useWeightedData)}</p>
          ${rowsHtml ? `<div class="map-tooltip-divider"></div>${rowsHtml}${extraHtml}` : ""}
        </div>
      `;
    };

    if (viewMode === "province") {
      const provinceName = feature.properties.prov_name_en;
      const bucket = getActiveBucket(feature);
      layer.bindTooltip(makeTooltip(provinceName, bucket), {
        sticky: true,
        direction: "top",
        opacity: 1,
        className: "map-region-tooltip",
      });
    } else {
      const feduid = normalizeRegionId(getFeduid(feature));
      const ridingName =
        feature.properties.name ||
        feature.properties.ED_NAME ||
        feature.properties.ENNAME ||
        (feduid ? `FEDUID ${feduid}` : "Riding");
      const bucket = getActiveBucket(feature);
      layer.bindTooltip(makeTooltip(ridingName, bucket), {
        sticky: true,
        direction: "top",
        opacity: 1,
        className: "map-region-tooltip",
      });
    }

    layer.on({
      mouseover: (e) => {
        e.target.setStyle(highlightStyle);
        e.target.bringToFront();
      },
      mouseout: (e) => {
        e.target.setStyle(styleFeature(e.target.feature));
      },
    });
  };

  const answerMapSignature = useMemo(
    () =>
      sortMappingEntries(answerDisplayMap)
        .map(
          ([answerId, meta]) => `${normalizeAnswerCode(answerId)}:${String(meta?.Display || "")}`
        )
        .join("|"),
    [answerDisplayMap]
  );

  const activeResponseSignature = useMemo(() => {
    const buckets = viewMode === "province" ? provinceCounts : ridingCounts;

    return buckets
      .map((bucket, index) => {
        const bucketId =
          viewMode === "province"
            ? bucket?.province
            : bucket?.riding || bucket?.ridingName || index;
        const responses = Object.entries(bucket?.byResp || {})
          .sort(([leftKey], [rightKey]) => {
            const leftNum = Number(leftKey);
            const rightNum = Number(rightKey);
            if (Number.isFinite(leftNum) && Number.isFinite(rightNum)) return leftNum - rightNum;
            return String(leftKey).localeCompare(String(rightKey));
          })
          .map(([answerId, count]) => `${normalizeAnswerCode(answerId)}=${Number(count) || 0}`)
          .join(",");

        return `${bucketId}:${responses}`;
      })
      .join("|");
  }, [provinceCounts, ridingCounts, viewMode]);

  // Force GeoJSON layer to remount when data, outcome, or answer labels change.
  const layerKey = useMemo(
    () => `${viewMode}-${outcomeKey || "none"}-${answerMapSignature}-${activeResponseSignature}`,
    [activeResponseSignature, answerMapSignature, outcomeKey, viewMode]
  );

  return (
    <div className="w-full h-full">
      <MapContainer className="h-full w-full" center={[62, -96]} zoom={4} scrollWheelZoom>
        <TileLayer
          attribution="© OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {geoJsonData && (
          <GeoJSON
            key={layerKey}
            data={geoJsonData}
            style={styleFeature}
            onEachFeature={onEachFeature}
          />
        )}
      </MapContainer>
    </div>
  );
};

export default InteractiveMap;

InteractiveMap.propTypes = {
  provinceCounts: PropTypes.arrayOf(
    PropTypes.shape({ province: PropTypes.number.isRequired, freq: PropTypes.number })
  ).isRequired,
  ridingCounts: PropTypes.arrayOf(
    PropTypes.shape({
      riding: PropTypes.number,
      ridingName: PropTypes.string,
      lookupKeys: PropTypes.arrayOf(PropTypes.string),
      freq: PropTypes.number,
    })
  ).isRequired,
  viewMode: PropTypes.oneOf(["province", "riding"]).isRequired,
  answerDisplayMap: PropTypes.objectOf(PropTypes.shape({ Display: PropTypes.string })).isRequired,
  useWeightedData: PropTypes.bool,
  depType: PropTypes.string,
  outcomeKey: PropTypes.string,
};

InteractiveMap.defaultProps = {
  useWeightedData: false,
  depType: "cat",
  outcomeKey: "",
};
